import ApiService from '@shared/services/api.service';

const changeEventsEndpoint = '/change_events';
const changeEventsSummaryEndpoint = '/change_events/summary';
const changeEventsConfigEndpoint = '/change_events/config';
const siteIncidentsEndpoint = '/site_incidents';
const changeEventsJobsEndpoint = '/change_events/jobs';

/**
 * Normalizes trailing `+00:00` to `Z` for ISO date strings. The API is not
 * consistent about which form it sends, and `moment` in Safari is fussy about
 * the difference. Same trick as web-crawl.service.js.
 * @param {unknown} value
 * @returns {unknown}
 */
function normalizeDate(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/\+00:00$/, 'Z');
}

/**
 * Shallow-clones `obj` and normalizes the listed date fields.
 * @param {object|null|undefined} obj
 * @param {string[]} keys
 * @returns {object|null|undefined}
 */
function normalizeDates(obj, keys) {
  if (!obj) return obj;
  const result = { ...obj };
  keys.forEach((key) => {
    if (result[key]) result[key] = normalizeDate(result[key]);
  });
  return result;
}

/** Date keys on a change event row. */
const eventDateFields = ['first_seen', 'last_seen'];
/** Date keys on a site incident row. */
const incidentDateFields = ['opened_at', 'last_alert_at', 'resolved_at'];
/** Date keys on an alert row inside an event. */
const alertDateFields = ['time_detected', 'detected_at', 'detected_time'];

/**
 * Normalizes the date on one of the small alert objects the detail endpoints
 * attach (`seed_alert`, `example_alert`).
 * @param {object|null|undefined} alert
 * @returns {object|null|undefined}
 */
function normalizeAlertRef(alert) {
  return alert ? normalizeDates(alert, alertDateFields) : alert;
}

/**
 * Normalizes the arrival times of an incident. The API sends plain timestamps,
 * oldest first, so the screen can draw when the alerts came in.
 * @param {*} arrivals
 * @returns {*}
 */
function normalizeArrivals(arrivals) {
  if (!Array.isArray(arrivals)) return arrivals;
  return arrivals.map((item) => {
    if (typeof item === 'string') return normalizeDate(item);
    // Tolerated in case the API later sends an object per arrival.
    return normalizeDates(item, ['at', 'time', 'detected_time', 'detected_at']);
  });
}

/**
 * Appends a repeatable array parameter using the PHP `foo[]=` convention, the
 * same way Incident.service.js does.
 * @param {string[]} parts query string parts, mutated
 * @param {string} name parameter name without the brackets
 * @param {Array<string|number>|undefined} values
 */
function appendArrayParam(parts, name, values) {
  if (!Array.isArray(values) || values.length === 0) return;
  values.forEach((value) => {
    parts.push(`${name}[]=${encodeURIComponent(value)}`);
  });
}

/**
 * Appends a single parameter when it holds a value.
 * @param {string[]} parts query string parts, mutated
 * @param {string} name
 * @param {string|number|null|undefined} value
 */
function appendParam(parts, name, value) {
  if (value === null || value === undefined || value === '') return;
  parts.push(`${name}=${encodeURIComponent(value)}`);
}

/**
 * HTTP client for Change Events (`/change_events/*`, `/site_incidents/*`).
 * Every method resolves the JSON body, because `ApiService` already unwraps
 * Axios `response.data`.
 *
 * Dates sent as `from` / `to` must already be UTC. The screens convert the
 * customer's picked dates before calling in.
 * @namespace
 */
const ChangeEventsService = {
  /**
   * `GET /change_events` — the paginated list of change events.
   *
   * The API refuses a range more than three months back.
   * @param {object} [params]
   * @param {number} [params.page] 1-based page number.
   * @param {number} [params.perPage] rows per page, sent as `per_page`.
   * @param {string} [params.from] UTC start, `YYYY-MM-DD HH:mm:ss`.
   * @param {string} [params.to] UTC end, `YYYY-MM-DD HH:mm:ss`.
   * @param {string} [params.search] matches the website name.
   * @param {string[]} [params.severity] any of `HIGH` `MEDIUM` `LOW` `unscored`.
   * @param {string[]} [params.impactType] any of `defacement` `availability` `others`.
   * @param {string[]} [params.eventClass] any of `sitewide_asset_change`
   *   `downtime_blast` `likely_deploy` `mixed` `isolated`. Sent as `class[]`.
   * @param {string} [params.status] `open` or `closed`.
   * @param {string} [params.site] exact website name.
   * @param {string} [params.sort] `alerts` `pages` `first_seen` `last_seen`.
   *   Sorting is done by the API — the table's own sorter only reorders the
   *   page you can already see.
   * @returns {Promise<{data?: object[], total?: number} & Record<string, *>>}
   */
  fetchList: async (params = {}) => {
    const parts = [];
    appendParam(parts, 'page', params.page);
    appendParam(parts, 'per_page', params.perPage);
    appendParam(parts, 'from', params.from);
    appendParam(parts, 'to', params.to);
    appendParam(parts, 'search', params.search);
    appendParam(parts, 'status', params.status);
    appendParam(parts, 'site', params.site);
    appendParam(parts, 'sort', params.sort);
    appendArrayParam(parts, 'severity', params.severity);
    appendArrayParam(parts, 'impact_type', params.impactType);
    appendArrayParam(parts, 'class', params.eventClass);
    const response = await ApiService.get(`${changeEventsEndpoint}?${parts.join('&')}`);
    if (response && Array.isArray(response.data)) {
      response.data = response.data.map((item) => normalizeDates(item, eventDateFields));
    }
    return response;
  },

  /**
   * `GET /change_events/{id}` — one change event, with the list fields plus the
   * example alert and the seed alert.
   * @param {string|number} id
   * @returns {Promise<object>}
   */
  fetchDetail: async (id) => {
    const response = await ApiService.get(`${changeEventsEndpoint}/${encodeURIComponent(id)}`);
    const event = normalizeDates(response && response.data ? response.data : response, eventDateFields);
    if (event) {
      if (event.seed_alert) event.seed_alert = normalizeAlertRef(event.seed_alert);
      if (event.example_alert) event.example_alert = normalizeAlertRef(event.example_alert);
      if (Array.isArray(event.alerts_preview)) {
        event.alerts_preview = event.alerts_preview.map((item) => normalizeDates(item, alertDateFields));
      }
    }
    return event;
  },

  /**
   * `GET /change_events/{id}/alerts` — the original alerts inside one event.
   *
   * Always paginated. One event can hold thousands of alerts, so never ask for
   * them all.
   * @param {string|number} id
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage] sent as `per_page`.
   * @returns {Promise<{data?: object[], total?: number} & Record<string, *>>}
   */
  fetchEventAlerts: async (id, params = {}) => {
    const parts = [];
    appendParam(parts, 'page', params.page);
    appendParam(parts, 'per_page', params.perPage);
    const response = await ApiService.get(`${changeEventsEndpoint}/${encodeURIComponent(id)}/alerts?${parts.join('&')}`);
    if (response && Array.isArray(response.data)) {
      response.data = response.data.map((item) => normalizeDates(item, alertDateFields));
    }
    return response;
  },

  /**
   * `GET /change_events/summary` — the counts for the Overview screen. The
   * database does the counting, so the reply is small.
   *
   * Returns `alerts`, `events`, `ratio`, `site_incidents`, `sites_affected`,
   * `by_day`, `by_severity` and `top_events`.
   * @param {object} [params]
   * @param {string} [params.from] UTC start.
   * @param {string} [params.to] UTC end.
   * @returns {Promise<object>}
   */
  fetchSummary: async (params = {}) => {
    const parts = [];
    appendParam(parts, 'from', params.from);
    appendParam(parts, 'to', params.to);
    const query = parts.length ? `?${parts.join('&')}` : '';
    const response = await ApiService.get(`${changeEventsSummaryEndpoint}${query}`);
    return response && response.data ? response.data : response;
  },

  /**
   * `GET /change_events/config` — the grouping and sending settings for the
   * logged-in customer. A customer with no row gets the defaults.
   * @returns {Promise<object>}
   */
  fetchConfig: async () => {
    const response = await ApiService.get(changeEventsConfigEndpoint);
    return response && response.data ? response.data : response;
  },

  /**
   * `PUT /change_events/config` — the only write in this version. It changes
   * settings, never data, and it applies from the next cycle.
   *
   * `backoff_minutes` is sent as a list of numbers; the API stores the string
   * form itself.
   * @param {object} data the full settings object.
   * @returns {Promise<object>}
   */
  updateConfig: async (data) => {
    const response = await ApiService.put(changeEventsConfigEndpoint, data);
    return response && response.data ? response.data : response;
  },

  /**
   * `GET /site_incidents` — websites that are, or were, in a bad state.
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage] sent as `per_page`.
   * @param {string} [params.from] UTC start.
   * @param {string} [params.to] UTC end.
   * @param {string} [params.status] `active` or `resolved`.
   * @param {string} [params.site] exact website name.
   * @param {string[]} [params.impactType] `defacement` or `availability`.
   * @returns {Promise<{data?: object[], total?: number} & Record<string, *>>}
   */
  fetchSiteIncidents: async (params = {}) => {
    const parts = [];
    appendParam(parts, 'page', params.page);
    appendParam(parts, 'per_page', params.perPage);
    appendParam(parts, 'from', params.from);
    appendParam(parts, 'to', params.to);
    appendParam(parts, 'status', params.status);
    appendParam(parts, 'site', params.site);
    appendArrayParam(parts, 'impact_type', params.impactType);
    const response = await ApiService.get(`${siteIncidentsEndpoint}?${parts.join('&')}`);
    if (response && Array.isArray(response.data)) {
      response.data = response.data.map((item) => normalizeDates(item, incidentDateFields));
    }
    return response;
  },

  /**
   * `POST /site_incidents/{id}/close` — close an open window by hand.
   *
   * Closing only. There is no reopen: a window is a period of time, and
   * reopening one would claim alerts arrived inside a period that had already
   * ended. A later burst opens a new window instead.
   *
   * The API answers 409 when the window is closed already, which happens when
   * two people have the screen open. The caller shows that message rather than
   * treating it as a failure.
   * @param {string|number} id
   * @returns {Promise<object>} the updated incident
   */
  closeSiteIncident: async (id) => {
    const response = await ApiService.post(`${siteIncidentsEndpoint}/${encodeURIComponent(id)}/close`, {});
    return response && response.data ? response.data : response;
  },

  /**
   * `GET /site_incidents/{id}` — one incident, plus the change events inside
   * it, the times its alerts arrived, and the pages worst hit.
   *
   * `arrivals` may be cut short, and the API then sets `arrivals_truncated`.
   * Any of these parts may be missing; the screen hides the panel rather than
   * showing an empty box.
   * @param {string|number} id
   * @returns {Promise<object>}
   */
  fetchSiteIncidentDetail: async (id) => {
    const response = await ApiService.get(`${siteIncidentsEndpoint}/${encodeURIComponent(id)}`);
    const body = response && response.data ? response.data : response;
    const incident = normalizeDates(body, incidentDateFields);
    if (incident) {
      if (Array.isArray(incident.change_events)) {
        incident.change_events = incident.change_events.map((item) => {
          const row = normalizeDates(item, eventDateFields);
          if (row && row.example_alert) row.example_alert = normalizeAlertRef(row.example_alert);
          return row;
        });
      }
      if (incident.arrivals) incident.arrivals = normalizeArrivals(incident.arrivals);
      // Alert Trail `detected_time` needs the same +00:00 → Z rewrite.
      if (Array.isArray(incident.serious_alerts)) {
        incident.serious_alerts = incident.serious_alerts.map((item) =>
          normalizeDates(item, alertDateFields)
        );
      }
    }
    return incident;
  },

  /**
   * `GET /change_events/jobs?customer_id=N` — the customer's recent backfill
   * and rollup requests, newest first. Admin only.
   * @param {number|string} customerId
   * @returns {Promise<{installed: boolean, service_alive?: boolean, jobs: object[]}>}
   */
  fetchJobs: async (customerId) => {
    const response = await ApiService.get(`${changeEventsJobsEndpoint}?customer_id=${encodeURIComponent(customerId)}`);
    // Same unwrapping as the other methods here, and a copy rather than a
    // mutation of the body.
    const body = (response && response.data ? response.data : response) || { installed: false, jobs: [] };
    return {
      ...body,
      jobs: (body.jobs || []).map((job) => normalizeDates(job, ['requested_at', 'started_at', 'finished_at']))
    };
  },

  /**
   * `POST /change_events/jobs` — queue a backfill or a rollup. Admin only.
   * A backfill needs `since` and `until` as `YYYY-MM-DD`; a rollup takes none.
   * The API answers 409 when one is already queued or running.
   * @param {{customer_id: number|string, mode: 'backfill'|'rollup', since?: string, until?: string}} body
   * @returns {Promise<{job: object, service_alive?: boolean}>}
   */
  requestJob: async (body) => {
    return ApiService.post(changeEventsJobsEndpoint, body);
  }
};

export default ChangeEventsService;
